import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccPost } from './mccApi';
import { PLATFORMS, CONTENT_TYPES, PRIORITIES } from './mccConstants';
import { PlatformIcon, Avatar } from './mccUi';

const commonReset = {
  title: '', description: '', hook: '', topic: '', target_audience: '',
  content_type: '', primary_platform: '', additional_platforms: [],
  person_in_content: '', call_to_action: '', priority: 'normal',
  requested_completion_date: '', assigned_to: [], related_campaign: '',
  problem_addressed: '', reference_links: '',
};

/**
 * Create Idea / Marketing Request / Project / Filming / Hook — one shared dialog
 * that adapts to the kind prop so we only pay for one modal shell.
 */
export default function MccCreateDialog({ open, onClose, kind, team, onCreated, defaultPlatform }) {
  const [form, setForm] = useState({ ...commonReset, primary_platform: defaultPlatform || '' });
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePlatform = (p) => {
    const set = new Set(form.additional_platforms || []);
    if (set.has(p)) set.delete(p); else set.add(p);
    upd('additional_platforms', Array.from(set));
  };
  const toggleAssignee = (id) => {
    const set = new Set(form.assigned_to || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    upd('assigned_to', Array.from(set));
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      let path = '/marketing/ideas';
      let payload = form;
      if (kind === 'request') {
        path = '/marketing/requests';
        payload = {
          title: form.title, description: form.description, reason: form.problem_addressed,
          desired_outcome: form.call_to_action, talent_required: form.person_in_content,
          assigned_to: form.assigned_to, content_type: form.content_type,
          primary_platform: form.primary_platform, additional_platforms: form.additional_platforms,
          target_audience: form.target_audience, priority: form.priority,
          due_date: form.requested_completion_date, reference_examples: form.reference_links,
        };
      } else if (kind === 'project') {
        path = '/marketing/projects';
        payload = {
          title: form.title, description: form.description, hook: form.hook,
          content_type: form.content_type, primary_platform: form.primary_platform,
          additional_platforms: form.additional_platforms, person_in_content: form.person_in_content,
          call_to_action: form.call_to_action, priority: form.priority,
          due_date: form.requested_completion_date, assigned_to: form.assigned_to,
          related_campaign: form.related_campaign,
        };
      } else if (kind === 'filming') {
        path = '/marketing/filming';
        payload = {
          title: form.title, person_being_filmed: form.person_in_content,
          topic: form.topic, script: form.description, talking_points: form.reference_links,
          hook: form.hook, primary_platform: form.primary_platform,
          additional_platforms: form.additional_platforms, filming_date: form.requested_completion_date,
          responsible_filmer_id: (form.assigned_to || [])[0] || null,
          related_campaign: form.related_campaign,
        };
      } else if (kind === 'hook') {
        path = '/marketing/hooks';
        payload = {
          hook: form.hook || form.title, topic: form.topic, target_audience: form.target_audience,
          content_type: form.content_type, platform: form.primary_platform,
          best_delivered_by: form.person_in_content, notes: form.description,
        };
      }
      const res = await mccPost(path, payload);
      toast.success(`Created`);
      setForm({ ...commonReset });
      onCreated?.(res);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const title = ({
    idea: 'Submit a new content idea',
    request: 'Submit a marketing request',
    project: 'Create a content project',
    filming: 'Schedule a filming session',
    hook: 'Add a hook to the library',
  })[kind] || 'Create';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {kind === 'idea' && 'Ideas start the pipeline. Once approved they convert to a content project.'}
            {kind === 'request' && 'Brokers, managers and team members can request videos, posts, ads, emails, websites or branding work here.'}
            {kind === 'project' && 'Create a project directly (skips the idea/request stage).'}
            {kind === 'filming' && 'Schedule a filming session — script, talent, location, date, editor.'}
            {kind === 'hook' && 'Save a hook so the team can search and reuse it later.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label={kind === 'hook' ? 'Hook line' : 'Title'} required>
            <Input value={kind === 'hook' ? (form.hook || form.title) : form.title}
              onChange={(e) => upd(kind === 'hook' ? 'hook' : 'title', e.target.value)}
              placeholder={kind === 'hook' ? 'e.g. "The bank\'s biggest secret nobody tells you…"' : 'Give this a clear, short title'}
              data-testid="mcc-create-title" />
          </Field>

          {kind !== 'hook' && (
            <Field label="Description">
              <Textarea rows={3} value={form.description} onChange={(e) => upd('description', e.target.value)}
                placeholder="What is this about? What do you need?" />
            </Field>
          )}

          {(kind === 'idea' || kind === 'project') && (
            <Field label="Hook (suggested)">
              <Textarea rows={2} value={form.hook} onChange={(e) => upd('hook', e.target.value)}
                placeholder="Opening line / scroll-stopper" />
            </Field>
          )}

          {kind === 'request' && (
            <Field label="Reason / desired outcome">
              <Textarea rows={2} value={form.problem_addressed} onChange={(e) => upd('problem_addressed', e.target.value)}
                placeholder="Why is this needed? What is the outcome?" />
            </Field>
          )}

          {kind === 'filming' && (
            <Field label="Talking points / notes">
              <Textarea rows={2} value={form.reference_links} onChange={(e) => upd('reference_links', e.target.value)}
                placeholder="Key talking points, references, links" />
            </Field>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Content type">
              <Select value={form.content_type || '__none__'} onValueChange={(v) => upd('content_type', v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {CONTENT_TYPES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Primary platform">
              <Select value={form.primary_platform || '__none__'} onValueChange={(v) => upd('primary_platform', v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.v} value={p.v}>
                      <span className="inline-flex items-center gap-2"><PlatformIcon platform={p.v} size={16} /> {p.l}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {kind !== 'hook' && (
            <Field label="Additional platforms">
              <div className="flex flex-wrap gap-1.5">
                {PLATFORMS.map((p) => {
                  const active = (form.additional_platforms || []).includes(p.v);
                  const Icon = p.icon;
                  return (
                    <button key={p.v} type="button" onClick={() => togglePlatform(p.v)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition ${
                        active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <Icon className="w-3 h-3" style={{ color: active ? 'inherit' : p.tone }} /> {p.l}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => upd('priority', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={kind === 'filming' ? 'Filming date' : 'Due date'}>
              <Input type="date" value={form.requested_completion_date} onChange={(e) => upd('requested_completion_date', e.target.value)} />
            </Field>
            <Field label={kind === 'hook' ? 'Best delivered by' : 'Person in content'}>
              <Input value={form.person_in_content} onChange={(e) => upd('person_in_content', e.target.value)} placeholder="Emmanuel / Rhianna / …" />
            </Field>
          </div>

          {kind !== 'hook' && (
            <Field label="Assign to team">
              <div className="flex flex-wrap gap-1.5">
                {team.map((m) => {
                  const active = (form.assigned_to || []).includes(m.id);
                  return (
                    <button
                      key={m.id} type="button" onClick={() => toggleAssignee(m.id)}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition ${
                        active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                      }`}
                      data-testid={`mcc-create-assign-${m.id}`}
                    >
                      <Avatar name={m.name} size={16} /> {m.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          {(kind === 'idea' || kind === 'project' || kind === 'filming') && (
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Related campaign">
                <Input value={form.related_campaign} onChange={(e) => upd('related_campaign', e.target.value)} placeholder="e.g. Winter Refinance 2026" />
              </Field>
              <Field label="Target audience">
                <Input value={form.target_audience} onChange={(e) => upd('target_audience', e.target.value)} placeholder="e.g. First-home buyers" />
              </Field>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="mcc-create-submit">
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}{required ? ' *' : ''}
      </p>
      {children}
    </div>
  );
}
